import {
  OrderByInput,
  useAccountListOrder,
  useSetAccountListOrder,
} from '@gripp/shared-logic';
import { InfiniteDataGrid } from '@gripp/shared-ui';
import React from 'react';
import { StyleSheet, View } from 'react-native';
import { useMaxHeight } from '../../shared/hooks/useMaxHeight';
import { useColumnsAccount } from './useColumnsAccount';

type AccountListProps = {
  filter: any;
  updateRowsSelected?: (rows: Array<any>) => void;
  showCheckboxColumn: boolean;
  showRolesColumn: boolean;
  showImpersonateWorkspace: boolean;
  showImpersonateUser: boolean;
  showGlobalAdminRole: boolean;
  onItemsCountChange?: (count: number) => void;
  addToGroupsComponent?: JSX.Element;
  workspaceId?: string;
};

export const AccountList = ({
  filter,
  updateRowsSelected,
  showCheckboxColumn,
  showRolesColumn,
  showImpersonateWorkspace,
  showImpersonateUser,
  showGlobalAdminRole,
  onItemsCountChange,
  addToGroupsComponent,
  workspaceId,
}: AccountListProps) => {
  const { containerMaxHeight, listMaxHeight } = useMaxHeight(64, 0, 228);

  const { data: accountOrder } = useAccountListOrder();
  const { mutateAsync: setAccountOrder } = useSetAccountListOrder();

  const { columns, exportColumns } = useColumnsAccount(
    showCheckboxColumn,
    showRolesColumn,
    showImpersonateWorkspace,
    showImpersonateUser,
    showGlobalAdminRole,
    workspaceId
  );

  const onOrderByChange = async (orderBy: OrderByInput) => {
    await setAccountOrder(orderBy);
  };

  return (
    <View
      style={[
        styles.accountsContainer,
        {
          maxHeight: containerMaxHeight,
        },
      ]}
    >
      <InfiniteDataGrid
        columns={columns}
        exportColumns={exportColumns}
        query={FIND_WORKSPACE_ACCOUNTS}
        dataPrefix="account.listAccounts"
        modelName="account"
        exportFileName='people'
        orderBy={accountOrder}
        pageSize={50}
        filter={filter}
        hideHeader={false}
        listMaxHeight={listMaxHeight}
        showScrollIndicator={true}
        updateRowsSelected={updateRowsSelected}
        onItemsCountChange={onItemsCountChange}
        onOrderByChange={onOrderByChange}
        headerContent={addToGroupsComponent}
      />
    </View>
  );
};

const FIND_WORKSPACE_ACCOUNTS = `
  query FindWorkspaceAccounts($filter: ListAccountsFilter, $page: PageRequest, $orderBy: OrderByInput) {
    account {
      listAccounts(filter: $filter, page: $page, orderBy: $orderBy) {
        count
        offset
        limit
        items {
          id
          createdAt
          name {
            first
            last
          }
          email
          phone {
            callingCode,
            countryCode,
            phoneNumber
          }
          location {
            type
            coordinates
          }
          lastScannedAddress
          jobTitle
          groups {
            id
            name
          }
          workspace {
            id
            name
            type
          }
          workspaceRoles {
            workspace
            role
          }
        }
      }
    }
  }
`;

const styles = StyleSheet.create({
  accountsContainer: {
    flex: 1,
    borderRadius: 8,
    overflow: 'hidden',
    zIndex: -1,
  },
});
